import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { isNil, sortBy, take } from "lodash";
import { Subject } from "rxjs";
import { map } from "rxjs/operators";
import {
    Fastighet,
    FastighetGeometry,
    Fastighetsagare,
    FastighetsagareAdress,
    PersonOrganisationsnummerStatusDto
} from "~app/common/components/map/components/menu/map-fastighet/map-fastighet-models";
import { MapFastighetService } from "~app/common/components/map/components/menu/map-fastighet/map-fastighet.service";
import { IMapGeometry, IMapGeometryWicket } from "~app/common/components/map/map.settings";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { environment } from "~environments/environment";

@Component({
    selector: "rask-vk-karta-fastigheter-popup",
    templateUrl: "./vk-karta-fastigheter-popup.component.html",
    styleUrls: ["./vk-karta-fastigheter-popup.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        TranslatePipe
    ]
})
export class VkKartaFastigheterPopupComponent implements OnDestroy {
    fastighetGeometri: IMapGeometry = null;
    fastighetMetria: Fastighet = null;
    importEnabled = false;

    protected downloading = false;

    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly mapFastighetService = inject(MapFastighetService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly raskModalService = inject(RaskModalService);
    private readonly translateService = inject(TranslateService);

    private raskkontakter: PersonOrganisationsnummerStatusDto[] = null;

    set data(value: any) {
        this.fastighetGeometri = value.geometry;
        this.importEnabled = value.importEnabled;
        this.init();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    protected clickImportLeverantor(): void {
        this.mapFastighetService.importFastighet(new FastighetGeometry(this.fastighetMetria, this.fastighetGeometri as IMapGeometryWicket));
        this.mapFastighetService.map.closeWidgets();
        this.mapFastighetService.map.closePopups();
    }

    protected consentedDownload(): void {
        const uuid = this.fastighetGeometri?.attributes?.objekt_id;
        this.getFastighet(uuid);
    }

    protected copyToClipboard(): void {
        ObjectUtils.copyToClipboard(this.getAsText(this.fastighetMetria));
    }

    protected downloadFromMetria(): any {
        const settings = new ModalSettings(this.translateService.instant("VIRKESKOP_ANBUD_KONTRAKT_MAP_POPUP_CONFIRM_HEADER"),
            this.translateService.instant("VIRKESKOP_ANBUD_KONTRAKT_MAP_POPUP_CONFIRM_BODY"));
        settings.contentIsMarkup = true;
        return this.raskModalService
            .confirm(settings)
            .subscribe((response) => {
                if (response === ConfirmDialogResponse.save) {
                    this.consentedDownload();
                }
            });
    }

    protected existsInRask(orgnummer: string): boolean {
        return this.raskkontakter?.some(o => o.personOrganisationsnummer === orgnummer);
    }

    protected getAdress1(agare: Fastighetsagare): string {
        const getPrimaryStreet = (adr: FastighetsagareAdress) => `${adr.utdelningsadress?.length > 0 ? adr.utdelningsadress[0] : ""} ${adr.postort}`;
        if (ObjectUtils.hasValue(agare?.folkbokforingsadress)) {
            return getPrimaryStreet(agare.folkbokforingsadress);
        } else if (ObjectUtils.hasValue(agare?.adress)) {
            return getPrimaryStreet(agare.adress);
        }
        return "";
    }

    protected getAsText(fastighet: Fastighet) {
        const agare = fastighet.fastighetsagare.map(fa =>
            `${this.getFastighetsagareNamn(fa)}\t${fa.andelTaljare}/${fa.andelNamnare}\t${this.getAdress1(fa)}\t${fa.personOrgNummer}`).join("\r\n");
        return `${fastighet.fastighetsbeteckning}\r\n------------------------------\r\n${agare}`;
    }

    protected getExtUrlSearch(agare: Fastighetsagare): string {
        return environment.eniroUrl.replace(
            "{params}",
            encodeURIComponent(`${this.getFastighetsagareNamn(agare)},${this.getAdress1(agare)}`));
    }

    protected getFastighet(uuid: string): void {
        this.downloading = true;
        this.raskkontakter = [];
        this.cdRef.detectChanges();
        this.mapFastighetService.getFastighet(uuid, true).subscribe(
            {
                next: (fst) => {
                    this.fastighetMetria = fst;
                    this.downloading = false;
                    this.cdRef.detectChanges();
                    requestAnimationFrame(() => {
                        this.mapFastighetService.map.updatePopupPosition();
                    });
                    this.mapFastighetService.addFastighet(new FastighetGeometry(this.fastighetMetria, this.fastighetGeometri as IMapGeometryWicket));
                    const orgnrList = fst.fastighetsagare.map(x => x.personOrgNummer);
                    this.mapFastighetService.getIsLeverantor(orgnrList).subscribe(
                        {
                            next: (kontakter) => {
                                this.raskkontakter = kontakter;
                                this.cdRef.detectChanges();
                            },
                            error: (e) => {
                                this.raskkontakter = [];
                            }
                        }
                    );
                },
                error: (e) => {
                    this.downloading = false;
                }
            }
        );
    }

    protected getFastighetsagare(limit: number): Fastighetsagare[] {
        return take(sortBy(this.fastighetMetria?.fastighetsagare, x => x?.namn), limit);
    }

    protected getFastighetsagareNamn(agare: Fastighetsagare): string {
        if (ObjectUtils.hasValue(agare?.namn)) {
            return agare.namn;
        } else if (ObjectUtils.hasValue(agare?.fornamn)) {
            return `${agare.fornamn} ${agare.efternamn}`;
        }
        return "";
    }

    protected hasMoreAgareThan(limit: number): boolean {
        return this.fastighetMetria?.fastighetsagare.length > limit;
    }

    protected init(): void {
        const uuid = this.fastighetGeometri?.attributes?.objekt_id;
        this.mapFastighetService.hasCached(uuid).pipe(map(isCachedFastighet => {
            if (isCachedFastighet === true) {
                this.getFastighet(uuid);
            }
        })).subscribe();
    }

    protected isDownloaded = () => !isNil(this.fastighetMetria);

    protected showImportOption = () => this.importEnabled === true;
}
